import {
  CheckoutBlockPageType,
  DiscountApplicationMethodType,
  DiscountApplicationType,
  DiscountUnitType,
  OfferDto,
  OfferPageType,
  QuantityButtonsUserInterface,
} from "@intelligems/ig-types";
import { sentenceToSnakeCase } from "@intelligems/ig-utils";
import { tierOneHasDiscount } from "@src/features/personalizations/components/offerModifications/userInterfaces/quantityButtons/utils/utils";

// The `offer_type` every offer event reports.
export const getOfferTypeKey = (offer: {
  discountApplicationType: DiscountApplicationType;
  discountApplicationMethod?: DiscountApplicationMethodType;
  unitType?: DiscountUnitType;
}): string =>
  sentenceToSnakeCase(
    getOfferName({
      discountApplicationType: offer.discountApplicationType,
      discountApplicationMethod: offer.discountApplicationMethod ?? "perItem",
      unitType: offer.unitType ?? "unit",
    })
  );

export const getOfferName = (
  offer: Pick<
    OfferDto,
    "discountApplicationType" | "discountApplicationMethod" | "unitType"
  >
): string => {
  if (!offer) return "";

  switch (offer.discountApplicationType) {
    case "cartDiscount":
      switch (offer.discountApplicationMethod) {
        case "perItem":
          return "Amount off products";
        case "perOrder":
          return "Amount off order";
        default:
          return "Amount off products";
      }
    case "tieredDiscount":
      return offer.unitType === "unit"
        ? "Quantity Discount"
        : "Threshold Discount";
    case "freeGift":
      return "Free Gift";
    case "freeShipping":
      return "Free Shipping";
    case "buyXGetY":
      return "Buy X Get Y";
    default:
      return "Amount off products";
  }
};

export const getNextButtonText = (
  drawerPage: OfferPageType,
  enabledSteps: OfferPageType[],
  hasAnyComponents: boolean,
  isLastPage: boolean,
  isCreate: boolean,
  isEdit: boolean
): string => {
  if (drawerPage === "enable") {
    return "Enable Offer";
  }
  const componentCount = enabledSteps.length;
  if (drawerPage === "settings") {
    if (hasAnyComponents) {
      if (componentCount === 1) {
        return "Continue to Component";
      } else {
        return `Continue to Component (1/${componentCount})`;
      }
    }
  }
  if (isLastPage) {
    if (!isCreate || isEdit) {
      return "Confirm Changes";
    }
    return hasAnyComponents ? "Create Offer" : "Confirm Changes";
  }
  if (drawerPage === "quantityButtons") {
    if (componentCount === 2) return "Continue to Component (2/2)";
    if (componentCount === 3) return "Continue to Component (2/3)";
  }
  if (drawerPage === "progressBar") {
    if (componentCount === 2) return "Continue to Component (2/2)";
    if (componentCount === 3) return "Continue to Component (3/3)";
  }
  return "Next";
};

export const getNextButtonTextCheckoutBlocks = (
  drawerPage: CheckoutBlockPageType,
  isLastPage: boolean,
  isCreate: boolean,
  isEdit: boolean
): string => {
  if (drawerPage === "enable") {
    return "Enable Checkout Experience";
  }

  if (isLastPage) {
    if (!isCreate || isEdit) {
      return "Confirm Changes";
    }
    return "Confirm Changes";
  }

  return "Next";
};

export const getQuantityButtonIndex = (
  currentUserInterface: QuantityButtonsUserInterface | null | undefined,
  offer: OfferDto,
  tierIndex: number
) => {
  const hasBaseButton =
    currentUserInterface?.desktopVariables?.buttons?.[0]?.quantity === 1 &&
    !tierOneHasDiscount(offer.tiers);

  const buttons = currentUserInterface?.desktopVariables?.buttons;
  const tier = offer.tiers[tierIndex];
  const buttonIndex =
    buttons?.findIndex((b) => b.quantity === tier?.minimumUnits) ?? -1;

  return {
    hasBaseButton,
    buttonIndex,
  };
};
